/**
 * Draft ⇄ API shapes for the request form. Drafts hold raw input strings so the form can
 * render exactly what the user typed; `toXxxInput` converts (and validates money) once on
 * submit, and `xxxDraftFrom` rehydrates a draft from a case view for editing.
 */
import {
  type BusinessEntityType,
  type CaseView,
  computePricing,
  type EquipmentCondition,
  type EquipmentItemView,
  type EquipmentTitleKind,
  type Money,
  MoneyError,
  type MoneyView,
  parseDecimal,
  PricingError,
  type PricingResult,
  type SoftCostKind,
  type SoftCostView,
} from '@tex/finance-shared'
import type { BuyerInput, DeliveryInput, EquipmentLineInput, PricingInput, SoftCostInput } from './cases'

export class FormError extends Error {
  constructor(
    readonly field: string,
    message: string,
  ) {
    super(message)
    this.name = 'FormError'
  }
}

export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY',
  'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH',
  'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
] as const

export const ENTITY_TYPE_LABELS: Record<BusinessEntityType, string> = {
  c_corp: 'C corporation',
  s_corp: 'S corporation',
  b_corp: 'B corporation',
  partnership: 'Partnership',
  sole_proprietorship: 'Sole proprietorship',
  llc: 'LLC',
  nonprofit: 'Nonprofit',
  municipality: 'Municipality',
  native_american_tribe: 'Native American tribe',
  cooperative: 'Cooperative',
  limited_partnership: 'Limited partnership',
  llp: 'LLP',
  lllp: 'LLLP',
  division: 'Division of another entity',
  trust: 'Trust',
  professional_corporation: 'Professional corporation',
  professional_association: 'Professional association',
}

export const SOFT_COST_LABELS: Record<SoftCostKind, string> = {
  tax: 'Sales tax',
  shipping: 'Freight / shipping',
  installation: 'Installation / setup',
  other: 'Other',
}

// ── drafts ──

export interface BuyerDraft {
  business_name: string
  contact_name: string
  contact_email: string
  contact_phone: string
  state: string
  equipment_use_state: string
  entity_type: BusinessEntityType | ''
  address: { line1: string; line2: string; city: string; state: string; postal_code: string }
}

export interface EquipmentDraft {
  key: string
  category: string
  make: string
  model: string
  model_year: string
  condition: EquipmentCondition
  quantity: string
  unit_price: string
  serial_number: string
  hours_or_miles: string
  listing_url: string
  title_kind: EquipmentTitleKind | ''
  vehicle_class: string
  existing_lien_noted: boolean
  description: string
}

export interface SoftCostDraft {
  key: string
  kind: SoftCostKind
  label: string
  amount: string
}

export interface PricingDraft {
  soft_costs: SoftCostDraft[]
  discount: string
  cash_contribution: string
  override_principal: boolean
  requested_principal: string
  reconciliation_reason: string
  trade_in_noted: boolean
}

export interface DeliveryDraft {
  expected_on: string
  pre_delivery_payment_requested: boolean
}

let seq = 0
export const draftKey = () => `d${++seq}`

export const emptyBuyer = (): BuyerDraft => ({
  business_name: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  state: '',
  equipment_use_state: '',
  entity_type: '',
  address: { line1: '', line2: '', city: '', state: '', postal_code: '' },
})

export const emptyEquipment = (): EquipmentDraft => ({
  key: draftKey(),
  category: '',
  make: '',
  model: '',
  model_year: '',
  condition: 'used',
  quantity: '1',
  unit_price: '',
  serial_number: '',
  hours_or_miles: '',
  listing_url: '',
  title_kind: '',
  vehicle_class: '',
  existing_lien_noted: false,
  description: '',
})

export const emptySoftCost = (kind: SoftCostKind = 'tax'): SoftCostDraft => ({ key: draftKey(), kind, label: '', amount: '' })

export const emptyPricing = (): PricingDraft => ({
  soft_costs: [],
  discount: '',
  cash_contribution: '',
  override_principal: false,
  requested_principal: '',
  reconciliation_reason: '',
  trade_in_noted: false,
})

export const emptyDelivery = (): DeliveryDraft => ({ expected_on: '', pre_delivery_payment_requested: false })

// ── view → draft ──

/** Integer-only: dividing minor units as a float would round large amounts by a cent. */
const decimal = (m: MoneyView): string => {
  const sign = m.amount_minor < 0 ? '-' : ''
  const abs = Math.abs(m.amount_minor)
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`
}

export function buyerDraftFrom(view: Extract<CaseView, { audience: 'dealer' | 'tex' }>): BuyerDraft {
  return {
    business_name: view.buyer_business_name,
    contact_name: view.buyer_contact_name,
    contact_email: view.buyer_contact_email,
    contact_phone: view.buyer_contact_phone ?? '',
    state: view.buyer_state ?? '',
    equipment_use_state: view.equipment_use_state ?? '',
    entity_type: view.buyer_entity_type ?? '',
    address: {
      line1: view.buyer_address?.line1 ?? '',
      line2: view.buyer_address?.line2 ?? '',
      city: view.buyer_address?.city ?? '',
      state: view.buyer_address?.state ?? '',
      postal_code: view.buyer_address?.postal_code ?? '',
    },
  }
}

export function equipmentDraftFrom(items: EquipmentItemView[]): EquipmentDraft[] {
  return items.map((e) => ({
    key: e.id,
    category: e.category,
    make: e.make ?? '',
    model: e.model ?? '',
    model_year: e.model_year?.toString() ?? '',
    condition: e.condition,
    quantity: e.quantity.toString(),
    unit_price: decimal(e.unit_price),
    serial_number: e.serial_number ?? '',
    hours_or_miles: e.hours_or_miles?.toString() ?? '',
    listing_url: e.listing_url ?? '',
    title_kind: e.title_kind ?? '',
    vehicle_class: e.vehicle_class ?? '',
    existing_lien_noted: e.existing_lien_noted,
    description: e.description ?? '',
  }))
}

export function pricingDraftFrom(view: CaseView, softCosts: SoftCostView[] = view.soft_costs): PricingDraft {
  const p = view.pricing
  return {
    soft_costs: softCosts.map((s) => ({ key: s.id, kind: s.kind, label: s.label ?? '', amount: decimal(s.amount) })),
    discount: p.discount.amount_minor ? decimal(p.discount) : '',
    cash_contribution: p.cash_contribution.amount_minor ? decimal(p.cash_contribution) : '',
    override_principal: p.reconciliation_reason !== null,
    requested_principal: p.reconciliation_reason !== null ? decimal(p.requested_principal) : '',
    reconciliation_reason: p.reconciliation_reason ?? '',
    trade_in_noted: p.trade_in_noted,
  }
}

export function deliveryDraftFrom(view: CaseView): DeliveryDraft {
  return {
    expected_on: view.delivery.expected_on ?? '',
    pre_delivery_payment_requested: view.delivery.pre_delivery_payment_requested,
  }
}

// ── draft → API ──

const blankToNull = (s: string): string | null => (s.trim() ? s.trim() : null)

function parseMoney(raw: string, field: string): Money {
  try {
    return parseDecimal(raw)
  } catch (err) {
    if (err instanceof MoneyError) throw new FormError(field, `${field}: enter a dollar amount like 12500.00`)
    throw err
  }
}

/** Optional money field: blank means "not set" (server default of 0). */
function parseOptionalMoney(raw: string, field: string): Money | undefined {
  return raw.trim() ? parseMoney(raw, field) : undefined
}

function parseInteger(raw: string, field: string, { min = 0 }: { min?: number } = {}): number | null {
  if (!raw.trim()) return null
  const n = Number(raw)
  if (!Number.isSafeInteger(n) || n < min) throw new FormError(field, `${field}: enter a whole number${min > 0 ? ` of at least ${min}` : ''}`)
  return n
}

const toView = (m: Money): MoneyView => ({ amount_minor: m.amountMinor, currency: m.currency })

export function toBuyerInput(d: BuyerDraft): BuyerInput {
  const addr = d.address
  const anyAddress = [addr.line1, addr.line2, addr.city, addr.state, addr.postal_code].some((s) => s.trim())
  if (anyAddress && !(addr.line1.trim() && addr.city.trim() && addr.state && addr.postal_code.trim())) {
    throw new FormError('address', 'Business address needs street, city, state and ZIP (or leave it all blank)')
  }
  return {
    business_name: d.business_name.trim(),
    contact_name: d.contact_name.trim(),
    contact_email: d.contact_email.trim(),
    contact_phone: blankToNull(d.contact_phone),
    state: d.state || null,
    equipment_use_state: d.equipment_use_state || null,
    entity_type: d.entity_type || null,
    address: anyAddress
      ? {
          line1: addr.line1.trim(),
          ...(addr.line2.trim() ? { line2: addr.line2.trim() } : {}),
          city: addr.city.trim(),
          state: addr.state,
          postal_code: addr.postal_code.trim(),
        }
      : null,
  }
}

export function toEquipmentInput(lines: EquipmentDraft[]): EquipmentLineInput[] {
  if (lines.length === 0) throw new FormError('equipment', 'Add at least one equipment line')
  return lines.map((e, i) => {
    const n = `Line ${i + 1}`
    if (!e.category.trim()) throw new FormError('equipment', `${n}: category is required`)
    const quantity = parseInteger(e.quantity, `${n} quantity`, { min: 1 })
    if (quantity === null) throw new FormError('equipment', `${n}: quantity is required`)
    if (!e.unit_price.trim()) throw new FormError('equipment', `${n}: unit price is required`)
    if (e.vehicle_class.trim() && e.title_kind !== 'vehicle') {
      throw new FormError('equipment', `${n}: vehicle class only applies when the title kind is "vehicle"`)
    }
    return {
      category: e.category.trim(),
      make: blankToNull(e.make),
      model: blankToNull(e.model),
      model_year: parseInteger(e.model_year, `${n} model year`),
      condition: e.condition,
      quantity,
      unit_price: toView(parseMoney(e.unit_price, `${n} unit price`)),
      serial_number: blankToNull(e.serial_number),
      hours_or_miles: parseInteger(e.hours_or_miles, `${n} hours/miles`),
      listing_url: blankToNull(e.listing_url),
      title_kind: e.title_kind || null,
      vehicle_class: e.title_kind === 'vehicle' ? blankToNull(e.vehicle_class) : null,
      existing_lien_noted: e.existing_lien_noted,
      description: blankToNull(e.description),
    }
  })
}

export function toPricingInput(d: PricingDraft): PricingInput {
  const soft_costs: SoftCostInput[] = d.soft_costs.map((s, i) => {
    if (!s.amount.trim()) throw new FormError('pricing', `Soft cost ${i + 1}: amount is required`)
    return { kind: s.kind, label: blankToNull(s.label), amount: toView(parseMoney(s.amount, `Soft cost ${i + 1}`)) }
  })
  const discount = parseOptionalMoney(d.discount, 'Discount')
  const cash = parseOptionalMoney(d.cash_contribution, 'Cash contribution')
  const requested = d.override_principal ? parseOptionalMoney(d.requested_principal, 'Requested amount') : undefined
  if (d.override_principal && !requested) throw new FormError('pricing', 'Requested amount: enter the amount to finance, or turn the override off')
  return {
    ...(soft_costs.length ? { soft_costs } : {}),
    ...(discount ? { discount: toView(discount) } : {}),
    ...(cash ? { cash_contribution: toView(cash) } : {}),
    ...(requested ? { requested_principal: toView(requested) } : {}),
    reconciliation_reason: d.override_principal ? blankToNull(d.reconciliation_reason) : null,
    trade_in_noted: d.trade_in_noted,
  }
}

export function toDeliveryInput(d: DeliveryDraft): DeliveryInput {
  return { expected_on: d.expected_on || null, pre_delivery_payment_requested: d.pre_delivery_payment_requested }
}

// ── live preview ──

export type PricingPreview = { ok: true; result: PricingResult } | { ok: false; message: string }

/**
 * Same arithmetic the API persists (`computePricing`), run on the drafts as typed. Lines
 * without a price yet are skipped so the totals fill in as the dealer goes.
 */
export function previewPricing(lines: EquipmentDraft[], pricing: PricingDraft): PricingPreview {
  try {
    const priced = lines.filter((l) => l.unit_price.trim())
    if (priced.length === 0) return { ok: false, message: 'Add an equipment line with a unit price to see totals.' }
    const equipment = priced.map((l, i) => {
      const qty = parseInteger(l.quantity, `Line ${i + 1} quantity`, { min: 1 })
      return { quantity: qty ?? 1, unitPrice: parseMoney(l.unit_price, `Line ${i + 1} unit price`) }
    })
    const softCosts = pricing.soft_costs.filter((s) => s.amount.trim()).map((s, i) => parseMoney(s.amount, `Soft cost ${i + 1}`))
    const requested = pricing.override_principal ? parseOptionalMoney(pricing.requested_principal, 'Requested amount') : undefined
    const result = computePricing({
      equipment,
      softCosts,
      discount: parseOptionalMoney(pricing.discount, 'Discount'),
      cashContribution: parseOptionalMoney(pricing.cash_contribution, 'Cash contribution'),
      requestedPrincipal: requested,
      reconciliationReason: pricing.override_principal ? pricing.reconciliation_reason : null,
    })
    return { ok: true, result }
  } catch (err) {
    if (err instanceof FormError) return { ok: false, message: err.message }
    if (err instanceof PricingError) return { ok: false, message: PRICING_MESSAGES[err.code] }
    if (err instanceof MoneyError) return { ok: false, message: 'An amount is too large to finance.' }
    throw err
  }
}

const PRICING_MESSAGES: Record<PricingError['code'], string> = {
  no_equipment: 'Add at least one equipment line.',
  quantity_invalid: 'Quantity must be a whole number of at least 1.',
  negative_amount: 'Amounts cannot be negative.',
  discount_exceeds_subtotal: 'Discount cannot exceed equipment plus soft costs.',
  cash_contribution_exceeds_invoice: 'Cash contribution cannot exceed the invoice total.',
  requested_principal_not_positive: 'There is nothing left to finance.',
  reconciliation_required: 'Explain why the requested amount differs from invoice minus cash.',
  reconciliation_without_override: 'A reconciliation note only applies when the requested amount is overridden.',
  currency_mismatch: 'All amounts must be in USD.',
}

/** Maps an API `pricing_invalid` reason back to the same copy as the preview. */
export function pricingReasonMessage(reason: string | undefined): string | null {
  if (!reason) return null
  if (reason === 'amount_overflow') return 'An amount is too large to finance.'
  const known = (Object.keys(PRICING_MESSAGES) as PricingError['code'][]).find((code) => code === reason)
  return known ? PRICING_MESSAGES[known] : null
}
